import React, { useContext, useEffect, useRef, useState } from "react";
import "./VideoCardAnimation.css";
import parse from "html-react-parser";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import useCustomTranslation from "../../utils/useCustomTranslation";
import { APP_COLORS } from "../../constants/styles";
import { WindowContext } from "../WindowResize/resize";
import { Link } from "react-router-dom";
import { getTargetFromType, useGetLink } from "../../utils/useOpenLink";

const VideoCardAnimation = ({ contents: contentsArray, colors: colorObj }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const { direction } = useCustomTranslation();

    const swiperCarousel = {
        spaceBetween: 10,
        slidesPerView: 1.1,
        modules: [Pagination, Navigation],
        pagination: { clickable: true, dynamicBullets: true, dynamicMainBullets: 7 },
        navigation: true,
        breakpoints: {
            768: {
                slidesPerView: 3,
            },
        },
        onSlideChange: (swiper) => setActiveIndex(swiper.realIndex),
    };

    return (
        <div className="py-30 videoCardWrapper" style={APP_COLORS(colorObj)}>
            <div className="container">
                <Swiper dir={direction} key={direction} {...swiperCarousel}>
                    {contentsArray.map((item, idx) => (
                        <SwiperSlide key={item.title}>
                            <HoverCard item={item} index={idx} isActive={idx === activeIndex} />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};

const HoverCard = ({ item, isActive }) => {
    const videoRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [showImage, setShowImage] = useState(true);
    const { isMobile } = useContext(WindowContext);
    const { getLink } = useGetLink();
    const path = item?.content_buttons?.[0]?.action || "";
    const type = item?.content_buttons?.[0]?.type || "";

    const handleMouseEnter = () => {
        setIsHovered(true);
        videoRef.current?.play();
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    useEffect(() => {
        if (!isMobile || !videoRef.current) return;

        let timeout;

        if (isActive) {
            setShowImage(true);
            timeout = setTimeout(() => {
                setShowImage(false);
                const playPromise = videoRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch((error) => {
                        console.warn("Video play aborted:", error);
                    });
                }
            }, 1000);
        } else {
            setShowImage(true);
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }

        return () => clearTimeout(timeout);
    }, [isActive, isMobile]);

    const shouldShowVideo = isHovered || (!showImage && isMobile && isActive);

    const isClickable = !!(path && type);

    const CardContent = (
        <div
            className={`hover-card ${isClickable ? "cursor-pointer" : "cursor-default"}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <img
                src={item?.content_image?.src}
                className={`card-image ${shouldShowVideo ? "hidden" : ""}`}
                alt={item?.title || "preview"}
            />
            <video
                ref={videoRef}
                src={item?.content_reference}
                muted
                loop
                playsInline
                className={`card-video ${shouldShowVideo ? "visible" : ""}`}
            />
            <div className="overlay">
                <h2>{item?.heading}</h2>
                {(isHovered || isMobile) && <p className="slide-up-text">{parse(item?.body)}</p>}
            </div>
        </div>
    );

    return isClickable ? (
        <Link to={getLink(path, type)} target={getTargetFromType(type, path)}>
            {CardContent}
        </Link>
    ) : (
        CardContent
    );
};

export default VideoCardAnimation;
